import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Checkbox, Typography, CircularProgress, TextField, Accordion, AccordionSummary, AccordionDetails, Chip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { RootState, useAppDispatch } from "../../store";
import { Agent, AgentType, EHR, Organization } from "../../types";
import { updateAgentThunk } from "../../slices/AgentSlice";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { InputStyles } from "../../styles/GeneralStyles";
import { Colors } from "../../Colors";
import FunctionalModulesLayout from "./FunctionalModulesLayout";
import { PrimaryButton } from "../../subcomponents/CustomButton";
import { updateOrganizationAttribute, setInboundRedirectNumber, getSession } from "../../slices/AuthSlice";
import { convertToCallablePhoneNumber } from "../../utils/utils";
import { StyledTextField } from "../../components/text-fields/StyledTextField";
import { NumberTextField } from "../../components/text-fields/NumberTextField";
import { SettingRow } from "./SettingRow";

const POLLING_WINDOWS = [
  { daysForward: 0, intervalMs: 4 * 60 * 1000 },
  { daysForward: 1, intervalMs: 9 * 60 * 1000 },
  { daysForward: 3, intervalMs: 19 * 60 * 1000 },
  { daysForward: 7, intervalMs: 29 * 60 * 1000 },
  { daysForward: 30, intervalMs: 59 * 60 * 1000 },
];

function PollingDebugPanel({ timestamps, onReset, onRefresh }: { timestamps?: Record<number, string>; onReset: () => void; onRefresh: () => void }) {
  return (
    <Accordion disableGutters sx={{ mt: 6, boxShadow: "none", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" color="text.secondary">
          Debug: Polling Timestamps
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {POLLING_WINDOWS.map(({ daysForward, intervalMs }) => {
            const iso = timestamps?.[daysForward];
            const lastPoll = iso ? DateTime.fromISO(iso) : null;
            const intervalMinutes = intervalMs / 60000;
            return (
              <Box key={daysForward} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 180 }}>
                  daysForward={daysForward} ({intervalMinutes}m interval)
                </Typography>
                {lastPoll ? (
                  <Chip size="small" label={lastPoll.toRelative()} title={lastPoll.toISO() ?? undefined} sx={{ fontFamily: "monospace", fontSize: 11 }} />
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    never
                  </Typography>
                )}
              </Box>
            );
          })}
          <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined" onClick={onRefresh}>
              Refresh
            </Button>
            <Button size="small" variant="outlined" color="warning" onClick={onReset}>
              Reset Timestamps
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default function Waitlist() {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const agents = useSelector((state: RootState) => state.agents.agents);
  const agent = useMemo(() => agents && agents.find((agent) => agent.agentType === AgentType.waitlist), [agents]);

  const [agentName, setAgentName] = useState<string | undefined>(undefined);
  const [callbackNumber, setCallbackNumber] = useState<string | undefined>(organization?.phoneNumber);
  const [inboundRedirectNumber, setInboundRedirectNumberState] = useState<string | undefined>(organization?.inboundRedirectNumber);
  const [emergencyTransferNumber, setEmergencyTransferNumberState] = useState<string | undefined>(organization?.emergencyTransferNumber);
  const [workingHoursStart, setWorkingHoursStart] = useState<DateTime | null>(null);
  const [workingHoursEnd, setWorkingHoursEnd] = useState<DateTime | null>(null);

  const [noLongerAvailableText, setNoLongerAvailableText] = useState<boolean>(false);
  const [autoStartWaitlistRuns, setAutoStartWaitlistRuns] = useState<boolean>(false);
  const [expireTime, setExpireTime] = useState<number | undefined>(undefined);
  const [daysForwardToCheckAvailableSlots, setDaysForwardToCheckAvailableSlots] = useState<number | undefined>(undefined);

  const [patientAdjustAvailability, setPatientAdjustAvailability] = useState<boolean>(false);

  const [minutesBetweenWaitlistGroups, setMinutesBetweenWaitlistGroups] = useState<number | undefined>(undefined);
  const [minutesBetweenPatients, setMinutesBetweenPatients] = useState<number | undefined>(undefined);
  const [randomPriority, setRandomPriority] = useState<boolean>(false);

  const [omitSameDayAppointments, setOmitSameDayAppointments] = useState<boolean>(false);
  const [allowSameDayAppointmentsAcrossProviderGroups, setAllowSameDayAppointmentsAcrossProviderGroups] = useState<boolean>(false);
  const [maxRunsPerPatient, setMaxRunsPerPatient] = useState<number | undefined>(undefined);
  const [maxRunsPerPatientPerDay, setMaxRunsPerPatientPerDay] = useState<number | undefined>(undefined);
  const [maxAppointmentsPerWeek, setMaxAppointmentsPerWeek] = useState<number | undefined>(undefined);
  const [gapBetweenAppointments, setGapBetweenAppointments] = useState<number | undefined>(undefined);
  const [defaultAppointmentDuration, setDefaultAppointmentDuration] = useState<number | undefined>(undefined);
  const [minutesToWaitBeforeStarting, setMinutesToWaitBeforeStarting] = useState<number | undefined>(undefined);
  const [disableInactivityPause, setDisableInactivityPause] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleResetPollingTimestamps = useCallback(async () => {
    if (!user?.token) return;
    try {
      await dispatch(updateOrganizationAttribute({ token: user.token, attribute: "webptPollingTimestamps", value: {} })).unwrap();
      showMessage("Polling timestamps reset", "success");
    } catch {
      showMessage("Failed to reset polling timestamps", "error");
    }
  }, [dispatch, user, showMessage]);

  const handleRefreshPollingTimestamps = useCallback(async () => {
    try {
      await dispatch(getSession()).unwrap();
      showMessage("Polling timestamps refreshed", "success");
    } catch {
      showMessage("Failed to refresh polling timestamps", "error");
    }
  }, [dispatch, showMessage]);

  useEffect(() => {
    if (agent) {
      setAgentName(agent.name);
      setNoLongerAvailableText(agent.omitNoLongerAvailableText || false);
      setAutoStartWaitlistRuns(agent.autoStartWaitlistRuns);
      setExpireTime(agent.minutesToCutoffBeforeAppointment);
      setDaysForwardToCheckAvailableSlots(agent.daysForwardToCheckAvailableSlots);
      setPatientAdjustAvailability(agent.patientAdjustAvailability || false);
      setMinutesBetweenWaitlistGroups(agent.minutesBetweenWaitlistGroups);
      setMinutesBetweenPatients(agent.minutesBetweenPatients);
      setRandomPriority(agent.randomPriority || false);
      setOmitSameDayAppointments(agent.omitSameDayAppointments || false);
      setMaxRunsPerPatient(agent.maxRunsPerPatient || 100);
      setMaxRunsPerPatientPerDay(agent.maxRunsPerPatientPerDay || 100);
      setMaxAppointmentsPerWeek(agent.maxAppointmentsPerWeek || 100);
      setGapBetweenAppointments(agent.gapBetweenAppointments || 0);
      setDefaultAppointmentDuration(agent.defaultAppointmentDuration || 30);
      setMinutesToWaitBeforeStarting(agent.minutesToWaitBeforeStarting || 0);
      setDisableInactivityPause(agent.disableInactivityPause || false);
    } else {
      setAgentName(undefined);
      setNoLongerAvailableText(false);
      setAutoStartWaitlistRuns(false);
      setExpireTime(undefined);
      setDaysForwardToCheckAvailableSlots(undefined);
      setPatientAdjustAvailability(false);
      setMinutesBetweenWaitlistGroups(undefined);
      setMinutesBetweenPatients(undefined);
      setRandomPriority(false);
      setOmitSameDayAppointments(false);
      setMaxRunsPerPatient(undefined);
      setMaxRunsPerPatientPerDay(undefined);
      setMaxAppointmentsPerWeek(undefined);
      setGapBetweenAppointments(undefined);
      setDefaultAppointmentDuration(undefined);
      setMinutesToWaitBeforeStarting(undefined);
      setDisableInactivityPause(false);
    }
  }, [agent]);

  useEffect(() => {
    if (organization) {
      setCallbackNumber(organization.phoneNumber);
      setInboundRedirectNumberState(organization.inboundRedirectNumber);
      setEmergencyTransferNumberState(organization.emergencyTransferNumber);
      setWorkingHoursStart(organization.workingHoursStart ? DateTime.fromISO(organization.workingHoursStart) : null);
      setWorkingHoursEnd(organization.workingHoursEnd ? DateTime.fromISO(organization.workingHoursEnd) : null);
      setAllowSameDayAppointmentsAcrossProviderGroups(organization.allowSameDayAppointmentsAcrossProviderGroups || false);
    }
  }, [organization]);

  useEffect(() => {
    if (gapBetweenAppointments !== undefined) {
      if (gapBetweenAppointments > 2) {
        setGapBetweenAppointments(2);
      } else if (gapBetweenAppointments < 0) {
        setGapBetweenAppointments(0);
      }
    }
  }, [gapBetweenAppointments]);

  const handleSave = async () => {
    if (!agent || !user?.token) {
      showMessage("Agent data or user session not found.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const agentProps: Partial<Agent> = {
        agentId: agent.agentId,
        name: agentName,
        omitNoLongerAvailableText: noLongerAvailableText,
        autoStartWaitlistRuns: autoStartWaitlistRuns,
        minutesToCutoffBeforeAppointment: expireTime,
        daysForwardToCheckAvailableSlots: daysForwardToCheckAvailableSlots,
        patientAdjustAvailability: patientAdjustAvailability,
        minutesBetweenWaitlistGroups: minutesBetweenWaitlistGroups,
        minutesBetweenPatients: minutesBetweenPatients,
        randomPriority: randomPriority,
        omitSameDayAppointments: omitSameDayAppointments,
        maxRunsPerPatient: maxRunsPerPatient,
        maxRunsPerPatientPerDay: maxRunsPerPatientPerDay,
        maxAppointmentsPerWeek: maxAppointmentsPerWeek,
        gapBetweenAppointments: gapBetweenAppointments,
        defaultAppointmentDuration: defaultAppointmentDuration,
        minutesToWaitBeforeStarting: minutesToWaitBeforeStarting,
        disableInactivityPause: disableInactivityPause,
      };

      const promises = [];

      promises.push(dispatch(updateAgentThunk({ token: user.token, agent: agentProps })).unwrap());

      if (callbackNumber) {
        promises.push(
          dispatch(
            updateOrganizationAttribute({
              token: user.token,
              attribute: "phoneNumber",
              value: convertToCallablePhoneNumber(callbackNumber),
            }),
          ).unwrap(),
        );
      }

      if (emergencyTransferNumber && emergencyTransferNumber.trim() !== "") {
        promises.push(
          dispatch(
            updateOrganizationAttribute({
              token: user.token,
              attribute: "emergencyTransferNumber",
              value: convertToCallablePhoneNumber(emergencyTransferNumber),
            }),
          ).unwrap(),
        );
      }

      if (inboundRedirectNumber && inboundRedirectNumber.trim() !== "") {
        promises.push(
          dispatch(
            setInboundRedirectNumber({
              token: user.token,
              inboundRedirectNumber: convertToCallablePhoneNumber(inboundRedirectNumber),
            }),
          ).unwrap(),
        );
      }

      if (workingHoursStart) {
        promises.push(
          dispatch(
            updateOrganizationAttribute({
              token: user.token,
              attribute: "workingHoursStart",
              value: workingHoursStart.toISO(),
            }),
          ).unwrap(),
        );
      }

      if (workingHoursEnd) {
        promises.push(
          dispatch(
            updateOrganizationAttribute({
              token: user.token,
              attribute: "workingHoursEnd",
              value: workingHoursEnd.toISO(),
            }),
          ).unwrap(),
        );
      }

      if (organization && allowSameDayAppointmentsAcrossProviderGroups !== (organization.allowSameDayAppointmentsAcrossProviderGroups || false)) {
        promises.push(
          dispatch(
            updateOrganizationAttribute({
              token: user.token,
              attribute: "allowSameDayAppointmentsAcrossProviderGroups",
              value: allowSameDayAppointmentsAcrossProviderGroups,
            }),
          ).unwrap(),
        );
      }

      await Promise.all(promises);
      showMessage("Waitlist settings updated successfully.", "success");
    } catch (error) {
      console.error("Failed to update waitlist settings:", error);
      showMessage("Failed to update waitlist settings.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!agent) {
    return (
      <FunctionalModulesLayout>
        <CircularProgress size={30} />
      </FunctionalModulesLayout>
    );
  }

  return (
    <FunctionalModulesLayout>
      <Box pb={6}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Waitlist
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" sx={{ mt: 1 }}>
            General Settings
          </Typography>
          <SettingRow label="Send text when appointment is no longer available">
            <Checkbox checked={!noLongerAvailableText} onChange={() => setNoLongerAvailableText(!noLongerAvailableText)} />
          </SettingRow>
          <SettingRow label="Automatically start waitlist runs" description="Recommended">
            <Checkbox checked={autoStartWaitlistRuns} onChange={() => setAutoStartWaitlistRuns(!autoStartWaitlistRuns)} />
          </SettingRow>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              Don't allow bookings
              <NumberTextField value={expireTime} onChange={(value) => setExpireTime(value)} />
              minutes before the appointment starts
            </Typography>
          </SettingRow>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              Check for waitlist availabilities
              <NumberTextField value={daysForwardToCheckAvailableSlots} onChange={(value) => setDaysForwardToCheckAvailableSlots(Math.min(value, 30))} inputProps={{ max: 30 }} />
              days forward (max 30 days)
            </Typography>
          </SettingRow>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              Default appointment duration:
              <NumberTextField value={defaultAppointmentDuration} onChange={(value) => setDefaultAppointmentDuration(value)} />
              minutes
            </Typography>
          </SettingRow>
          <Typography variant="h6" sx={{ mt: 4 }}>
            Additional Functionality
          </Typography>
          <SettingRow label="Allow patient to adjust waitlist availability" description="Recommended">
            <Checkbox checked={patientAdjustAvailability} onChange={() => setPatientAdjustAvailability(!patientAdjustAvailability)} />
          </SettingRow>

          <Typography variant="h6" sx={{ mt: 4 }}>
            Contact Delay
          </Typography>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              Minutes delay before sending texts:
              <NumberTextField value={minutesToWaitBeforeStarting} onChange={(value) => setMinutesToWaitBeforeStarting(value)} />
            </Typography>
          </SettingRow>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              Wait
              <NumberTextField value={minutesBetweenWaitlistGroups} onChange={(value) => setMinutesBetweenWaitlistGroups(value)} />
              minutes until contacting the next priority group
            </Typography>
          </SettingRow>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              Wait
              <NumberTextField value={minutesBetweenPatients} onChange={(value) => setMinutesBetweenPatients(value)} />
              minutes between each patient
            </Typography>
          </SettingRow>
          <SettingRow label="Randomize patient priority within each group">
            <Checkbox checked={randomPriority} onChange={() => setRandomPriority(!randomPriority)} />
          </SettingRow>
          <Typography variant="h6" sx={{ mt: 4 }}>
            Patient Eligibility Criteria
          </Typography>
          <SettingRow label="Skip patients with same day appointments" description="Recommended">
            <Checkbox checked={omitSameDayAppointments} onChange={() => setOmitSameDayAppointments(!omitSameDayAppointments)} />
          </SettingRow>
          <SettingRow
            label="Never pause patients for inactivity"
            description={
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  When enabled, patients keep receiving waitlist texts regardless of how long it's been since they last responded. (By default, patients are paused after 30 days of not interacting
                  with the bot.)
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <WarningAmberIcon sx={{ fontSize: "16px", color: Colors.warning }} />
                  <Typography variant="body2" sx={{ color: Colors.warning, fontWeight: 600 }}>
                    Do not turn this on without contacting us first.
                  </Typography>
                </Box>
              </Box>
            }
          >
            <Checkbox checked={disableInactivityPause} onChange={() => setDisableInactivityPause(!disableInactivityPause)} />
          </SettingRow>
          <SettingRow
            label="Allow same-day appointments across provider groups"
            description="When enabled, patients can book a same-day appointment for a different case study, as long as the providers are in different provider groups."
          >
            <Checkbox checked={allowSameDayAppointmentsAcrossProviderGroups} onChange={() => setAllowSameDayAppointmentsAcrossProviderGroups(!allowSameDayAppointmentsAcrossProviderGroups)} />
          </SettingRow>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              Each patient can only have
              <NumberTextField value={maxRunsPerPatient} onChange={(value) => setMaxRunsPerPatient(value)} />
              runs active per week
            </Typography>
          </SettingRow>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              Each patient can only have
              <NumberTextField value={maxRunsPerPatientPerDay} onChange={(value) => setMaxRunsPerPatientPerDay(value)} />
              runs active per day
            </Typography>
          </SettingRow>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              Each patient can have a maximum of
              <NumberTextField value={maxAppointmentsPerWeek} onChange={(value) => setMaxAppointmentsPerWeek(value)} />
              appointments booked per week
            </Typography>
          </SettingRow>
          <SettingRow>
            <Typography variant="body1" display="flex" alignItems="center" gap={1}>
              The gap between appointments should be at least
              <NumberTextField value={gapBetweenAppointments} onChange={(value) => setGapBetweenAppointments(value)} />
              days (max 2 days)
            </Typography>
          </SettingRow>
          <Box mt={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <PrimaryButton onClick={handleSave} disabled={isLoading}>
              {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
            </PrimaryButton>
          </Box>
          <PollingDebugPanel timestamps={organization?.webptPollingTimestamps} onReset={handleResetPollingTimestamps} onRefresh={handleRefreshPollingTimestamps} />
        </Box>
      </Box>
    </FunctionalModulesLayout>
  );
}
